/**
 * Parallax utility for blocks with data attributes
 * Intelligently targets elements based on block type
 *
 * Features:
 * - Supports vertical, horizontal, and directional (up, down, left, right) parallax
 * - Supports scale and opacity parallax effects
 *
 * Usage:
 * Add data attributes to any block to enable parallax effects:
 * - data-parallax-speed: Speed of the parallax effect (e.g., 0.5)
 * - data-parallax-direction: Direction of movement (vertical, horizontal, up, down, left, right)
 * - data-parallax-scale: Target scale value (e.g., 1.2)
 * - data-parallax-opacity: Target opacity value (e.g., 0.8)
 * - data-parallax-start: ScrollTrigger start position (e.g., "top bottom")
 * - data-parallax-end: ScrollTrigger end position (e.g., "bottom top")
 */

import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

/**
 * Get the target element for parallax based on block type
 * @param {HTMLElement} block - The block element
 * @returns {HTMLElement|null} - The target element to animate
 */
function getParallaxTarget(block) {
  // Check if this is a cover block
  if (block.classList.contains('wp-block-cover')) {
    // Target the image background element
    const imageBackground = block.querySelector('.wp-block-cover__image-background');
    return imageBackground || block;
  }

  // Check if this is a group block
  if (block.classList.contains('wp-block-group')) {
    // Target the block wrapper itself
    return block;
  }

  // For other blocks, target the block itself
  return block;
}

/**
 * Parse parallax data attributes from a block
 * @param {HTMLElement} block - The block element
 * @returns {Object|null} - Parallax configuration object or null
 */
function getParallaxConfig(block) {
  const speed = block.dataset.parallaxSpeed;
  const direction = block.dataset.parallaxDirection || 'vertical';
  const start = block.dataset.parallaxStart || 'top bottom';
  const end = block.dataset.parallaxEnd || 'bottom top';
  const scale = block.dataset.parallaxScale;
  const opacity = block.dataset.parallaxOpacity;

  // If no parallax attributes are found, return null
  if (!speed && !scale && !opacity) {
    return null;
  }

  return {
    speed: parseFloat(speed) || 0,
    direction,
    start,
    end,
    scale: scale ? parseFloat(scale) : null,
    opacity: opacity ? parseFloat(opacity) : null,
  };
}

/**
 * Apply parallax effect to a block
 * @param {HTMLElement} block - The block element
 */
function applyParallax(block) {
  const config = getParallaxConfig(block);
  if (!config) return;

  const target = getParallaxTarget(block);
  if (!target) return;

  // Check if this element already has a parallax animation
  // If so, kill it before creating a new one
  const existingTriggers = ScrollTrigger.getAll().filter(trigger => trigger.trigger === block);
  existingTriggers.forEach(trigger => trigger.kill());

  // Build the animation object
  const animation = {};

  // Handle movement parallax
  if (config.speed !== 0) {
    const distance = config.speed * 100;

    switch (config.direction) {
      case 'vertical':
      case 'up':
        animation.y = -distance;
        break;
      case 'down':
        animation.y = distance;
        break;
      case 'left':
        animation.x = -distance;
        break;
      case 'right':
        animation.x = distance;
        break;
      case 'horizontal':
        animation.x = distance;
        break;
    }
  }

  // Handle scale parallax
  if (config.scale !== null) {
    animation.scale = config.scale;
  }

  // Handle opacity parallax
  if (config.opacity !== null) {
    animation.opacity = config.opacity;
  }

  // Add will-change CSS property for better performance
  target.style.willChange = 'transform';

  // Create the ScrollTrigger animation
  gsap.to(target, {
    ...animation,
    ease: 'none',
    scrollTrigger: {
      trigger: block,
      start: config.start,
      end: config.end,
      scrub: 0.5, // Smooth out the parallax with 0.5 second delay
      invalidateOnRefresh: true,
      onLeave: () => {
        // Remove will-change when animation completes
        target.style.willChange = 'auto';
      },
      onEnterBack: () => {
        // Re-add will-change when scrolling back
        target.style.willChange = 'transform';
      },
    },
  });
}

/**
 * Initialize parallax for all blocks with parallax data attributes
 */
function initParallax() {
  // Find all blocks with any parallax data attribute
  const parallaxBlocks = document.querySelectorAll('[data-parallax-speed], [data-parallax-scale], [data-parallax-opacity]');

  parallaxBlocks.forEach(block => {
    // Skip if this element is inside a Swiper container
    if (block.closest('.swiper-container, .swiper, .wp-block-content-slider')) {
      return;
    }

    // Skip if this element has Swiper parallax attributes
    if (block.hasAttribute('data-swiper-parallax')) {
      return;
    }

    applyParallax(block);
  });
}

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
  initParallax();

  // Refresh ScrollTrigger after a delay to account for dynamic content
  // This ensures Swiper and other dynamic elements are fully initialized
  setTimeout(() => {
    ScrollTrigger.refresh();
  }, 500);
}, { passive: true });

// Re-initialize on window resize (debounced)
let resizeTimeout;
window.addEventListener('resize', () => {
  clearTimeout(resizeTimeout);
  resizeTimeout = setTimeout(() => {
    ScrollTrigger.refresh();
  }, 250);
}, { passive: true });

export { initParallax, applyParallax, getParallaxTarget, getParallaxConfig };
